<template>
  <div class="coupon-float" :class="{ 'coupon-float--collapsed': collapsed }" @click="toggle">
    <div class="coupon-float__tag" v-if="collapsed">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
        <path d="M21 5H3a2 2 0 00-2 2v4a2 2 0 002 2h0a2 2 0 010 4h0a2 2 0 00-2 2v2a2 2 0 002 2h18a2 2 0 002-2v-2a2 2 0 00-2-2h0a2 2 0 010-4h0a2 2 0 002-2V7a2 2 0 00-2-2z"/>
      </svg>
      <span>20% OFF</span>
    </div>
    <div class="coupon-float__card" v-else>
      <div class="coupon-float__close" @click.stop="collapsed = true">&times;</div>
      <div class="coupon-float__discount">{{ $t('home.couponTitle') }}</div>
      <div class="coupon-float__countdown">
        <span class="countdown-item">{{ countdown.hours }}<small>h</small></span>
        <span class="countdown-sep">:</span>
        <span class="countdown-item">{{ countdown.minutes }}<small>m</small></span>
        <span class="countdown-sep">:</span>
        <span class="countdown-item">{{ countdown.seconds }}<small>s</small></span>
      </div>
      <div class="coupon-float__code" @click.stop="copyCoupon">
        <span class="code-text">SAVE20</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
        </svg>
      </div>
      <div class="coupon-float__hint">{{ copied ? $t('home.couponCopied') : $t('home.couponHint') }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CouponFloat',
  props: {
    defaultCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      collapsed: this.defaultCollapsed || window.innerWidth <= 768,
      copied: false,
      countdown: { hours: '23', minutes: '59', seconds: '59' },
      countdownTimer: null
    }
  },
  mounted () {
    this.startCountdown()
  },
  beforeDestroy () {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  },
  methods: {
    toggle () {
      if (this.collapsed) {
        this.collapsed = false
      }
    },
    copyCoupon () {
      const text = 'SAVE20'
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.copied = true
          setTimeout(() => { this.copied = false }, 2000)
        })
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      }
    },
    startCountdown () {
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      const update = () => {
        const now = new Date()
        const diff = Math.max(0, end - now)
        const h = Math.floor(diff / 3600000)
        const m = Math.floor((diff % 3600000) / 60000)
        const s = Math.floor((diff % 60000) / 1000)
        this.countdown = {
          hours: String(h).padStart(2, '0'),
          minutes: String(m).padStart(2, '0'),
          seconds: String(s).padStart(2, '0')
        }
      }
      update()
      this.countdownTimer = setInterval(update, 1000)
    }
  }
}
</script>

<style lang="scss">
@import '../styles/variables';

.coupon-float {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 100;
  cursor: pointer;
  transition: all 0.3s ease;

  &--collapsed {
    bottom: 24px;
  }
}

.coupon-float__tag {
  display: flex;
  align-items: center;
  gap: 6px;
  background: $el-color-primary;
  color: #fff;
  padding: 10px 16px;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 16px rgba(81, 45, 202, 0.3);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
}

.coupon-float__card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  position: relative;
  text-align: center;
}

.coupon-float__close {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
}

.coupon-float__discount {
  font-size: 1.5rem;
  font-weight: 800;
  color: $el-color-primary;
  margin-bottom: 10px;
}

.coupon-float__countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 12px;

  .countdown-item {
    background: #1a1a2e;
    color: #fff;
    padding: 4px 8px;
    border-radius: 6px;
    font-weight: 700;
    font-size: 1rem;
    min-width: 36px;

    small {
      font-size: 0.7rem;
      opacity: 0.7;
      margin-left: 1px;
    }
  }
  .countdown-sep {
    font-weight: 700;
    color: #374151;
  }
}

.coupon-float__code {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f3f0ff;
  border: 2px dashed $el-color-primary;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;

  .code-text {
    font-weight: 800;
    font-size: 1.125rem;
    color: $el-color-primary;
    letter-spacing: 0.1em;
  }

  svg { color: $el-color-primary; }
}

.coupon-float__hint {
  margin-top: 8px;
  font-size: 0.8125rem;
  color: #6b7280;
  text-align: center;
}

@media (max-width: 768px) {
  .coupon-float {
    bottom: 16px;
    left: 16px;
  }
  .coupon-float__card {
    min-width: 190px;
    padding: 16px 18px;
  }
}
</style>
